import React from 'react';
import {
  Typography,
  Box,
  Grid,
  useTheme,
  Chip
} from '@mui/material';
import { ResponsiveBar } from '@nivo/bar';
import EmptyState from '../EmptyState';
import { formatCurrency, formatNumber, formatYAxisValue } from './formatters';

interface CfopData {
  cfop: string;
  total_icms: number | string;
  total_transacoes: number | string;
}

interface DrillDownCfopChartProps {
  top_cfops: CfopData[];
  title?: string;
}

const DrillDownCfopChart: React.FC<DrillDownCfopChartProps> = ({ 
  top_cfops, 
  title = "Análise por CFOP (até 5 registros)" 
}) => {
  const theme = useTheme();

  // Filtrar e preparar dados dos CFOPs
  const cfopsValidos = (top_cfops || []).filter(item => {
    const temCfop = item && item.cfop;
    const valorNumerico = Number(item.total_icms);
    const temTotalIcms = !isNaN(valorNumerico);
    const valorPositivo = valorNumerico > 0;
    
    return temCfop && temTotalIcms && valorPositivo;
  });
  
  const cfopData = cfopsValidos.length > 0
    ? cfopsValidos
        .slice(0, 8)
        .sort((a, b) => Number(b.total_icms) - Number(a.total_icms)) // Ordenar por ICMS em ordem decrescente (maior primeiro)
        .map(item => ({
          cfop: item.cfop,
          ICMS: Number(item.total_icms),
          transacoes: Number(item.total_transacoes)
        }))
    : [];

  return (
    <Grid item xs={12}>
      <Typography variant="subtitle1" gutterBottom>
        {title}
        {cfopsValidos.length > 0 && (
          <Chip 
            label={`${formatNumber(cfopsValidos.length)} CFOPs`} 
            size="small" 
            sx={{ ml: 1 }} 
            color="primary"
            variant="outlined"
          />
        )}
      </Typography>
      <Box sx={{ height: 400 }}>
        {cfopsValidos.length > 0 ? (
          <ResponsiveBar
            data={cfopData}
            keys={['ICMS']}
            indexBy="cfop"
            margin={{ top: 20, right: 100, bottom: 60, left: 100 }}
            padding={0.3}
            colors={theme.palette.primary.main}
            axisTop={null}
            axisRight={null}
            axisBottom={{
              tickSize: 5,
              tickPadding: 5,
              legend: 'CFOP',
              legendOffset: 40,
              legendPosition: 'middle'
            }}
            axisLeft={{
              tickSize: 5,
              tickPadding: 5,
              legend: 'Valor ICMS (R$)',
              legendOffset: -80,
              legendPosition: 'middle',
              format: formatYAxisValue
            }}
            labelSkipWidth={12}
            labelSkipHeight={12}
            labelTextColor="#fff"
            valueFormat={(value) => formatCurrency(value as number)}
            tooltip={({ value, indexValue, data }) => {
              const cfopInfo = cfopData.find(item => item.cfop === indexValue);
              return (
                <Box
                  sx={{
                    background: theme.palette.background.paper,
                    borderRadius: 1,
                    boxShadow: 2,
                    p: 2,
                    minWidth: 200
                  }}
                >
                  <Typography variant="body2" fontWeight={500}>
                    CFOP: {indexValue}
                  </Typography>
                  <Typography variant="h6" color="primary.main">
                    {formatCurrency(value as number)}
                  </Typography>
                  {cfopInfo && (
                    <Typography variant="caption" color="text.secondary">
                      {formatNumber(cfopInfo.transacoes)} transações
                    </Typography>
                  )}
                </Box>
              );
            }}
          />
        ) : (
          <EmptyState
            icon="📊"
            title="Sem dados de CFOP"
            description="Este produto não possui operações fiscais registradas"
            height={400}
          />
        )}
      </Box>
    </Grid>
  );
};

export default DrillDownCfopChart; 